import {
    METADATA_KINDS,
    permissionState,
    resetPermissions,
} from "@app/store/permissionStore.svelte.js";
import { QUICK_TOOLS_PANEL_IDS, uiState } from "@app/store/uiStore.svelte.js";
import { normalizePrimaryToolbarConfig } from "@app/lib/toolbar/primaryToolbarConfig.js";
import { configureSecondaryToolbar } from "@app/lib/toolbar/secondaryToolbarConfig.svelte.js";

export const ROLES = ["guest", "reviewer", "editor"];

const NO_EDITING_MENUS = {
    edit: false,
    format: false,
    timecode: false,
    insert: false,
    aiTools: false,
};

function allMetadata(value) {
    return Object.fromEntries(METADATA_KINDS.map((kind) => [kind, value]));
}

/*
 * guest:    preview and playback only.
 * reviewer: preview, playback and metadata; text and timing are locked.
 * editor:   full access (the default).
 */
const ROLE_PRESETS = {
    guest: () => ({
        editingLocked: true,
        metadata: allMetadata(false),
        primaryToolbar: { menus: { ...NO_EDITING_MENUS }, buttons: {} },
        secondaryToolbar: { left: [], center: [], right: [] },
        quickToolsPanel: false,
        quickToolsAllowed: null,
        timeline: true,
    }),
    reviewer: () => ({
        editingLocked: true,
        metadata: allMetadata(true),
        primaryToolbar: {
            menus: { ...NO_EDITING_MENUS, edit: ["edit-undo", "edit-redo", "edit-goto"] },
            buttons: {},
        },
        secondaryToolbar: { left: ["undo", "redo", "shortcuts"], center: [], right: [] },
        quickToolsPanel: true,
        quickToolsAllowed: [
            "summary",
            "qcAndReview",
            "manualQc",
            "errors",
            "speakers",
            "tags",
            "markers",
            "notes",
        ],
        timeline: true,
    }),
    editor: () => ({
        editingLocked: false,
        metadata: allMetadata(true),
        primaryToolbar: null,
        secondaryToolbar: null,
        quickToolsPanel: true,
        quickToolsAllowed: null,
        timeline: true,
    }),
};

function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Normalize a list of QuickTools panel ids.
 * @returns {{ panels: string[] | null, ignored: string[] }}
 */
export function normalizeQuickToolsPanels(panelIds) {
    if (panelIds === null || panelIds === undefined) {
        return { panels: null, ignored: [] };
    }
    if (!Array.isArray(panelIds)) {
        throw new Error("QuickTools panels must be an array of panel ids, null, or undefined.");
    }

    const panels = [];
    const ignored = [];
    panelIds.forEach((rawId) => {
        const id = QUICK_TOOLS_PANEL_IDS.find(
            (panelId) => panelId.toLowerCase() === String(rawId).trim().toLowerCase(),
        );
        if (id && !panels.includes(id)) {
            panels.push(id);
        } else {
            ignored.push(String(rawId));
        }
    });
    return { panels, ignored };
}

function mergeOverrides(preset, overrides) {
    if (overrides === undefined || overrides === null) {
        return preset;
    }
    if (!isPlainObject(overrides)) {
        throw new Error("Role overrides must be an object.");
    }

    const merged = { ...preset, ...overrides };
    if (isPlainObject(overrides.metadata)) {
        merged.metadata = { ...preset.metadata, ...overrides.metadata };
    }
    if (isPlainObject(overrides.primaryToolbar) && preset.primaryToolbar) {
        merged.primaryToolbar = {
            menus: { ...preset.primaryToolbar.menus, ...(overrides.primaryToolbar.menus ?? {}) },
            buttons: { ...preset.primaryToolbar.buttons, ...(overrides.primaryToolbar.buttons ?? {}) },
        };
    }
    return merged;
}

/**
 * Apply a predefined role, optionally adjusted with overrides that use the
 * same shape as the preset (editingLocked, metadata, primaryToolbar,
 * secondaryToolbar, quickToolsPanel, quickToolsAllowed, timeline).
 * @param {"guest"|"reviewer"|"editor"} role
 * @param {object} [overrides]
 */
export function applyRole(role, overrides) {
    const presetFactory = ROLE_PRESETS[role];
    if (!presetFactory) {
        throw new Error(`Unknown role: ${role}. Expected one of ${ROLES.join(", ")}.`);
    }

    const settings = mergeOverrides(presetFactory(), overrides);
    const ignored = [];

    const { config: primaryToolbar, ignored: toolbarIgnored } =
        normalizePrimaryToolbarConfig(settings.primaryToolbar);
    ignored.push(...toolbarIgnored.map((id) => `primaryToolbar.${id}`));

    const { panels, ignored: panelsIgnored } = normalizeQuickToolsPanels(settings.quickToolsAllowed);
    ignored.push(...panelsIgnored.map((id) => `quickToolsAllowed.${id}`));

    const metadata = allMetadata(true);
    if (isPlainObject(settings.metadata)) {
        Object.entries(settings.metadata).forEach(([kind, value]) => {
            if (METADATA_KINDS.includes(kind) && typeof value === "boolean") {
                metadata[kind] = value;
            } else {
                ignored.push(`metadata.${kind}`);
            }
        });
    }

    resetPermissions();
    permissionState.editingLocked = Boolean(settings.editingLocked);
    permissionState.metadata = metadata;
    permissionState.primaryToolbar = primaryToolbar;
    permissionState.quickToolsPanel = settings.quickToolsPanel !== false;
    permissionState.quickToolsAllowed = panels;
    permissionState.timeline = settings.timeline !== false;
    permissionState.role = role;

    const secondaryResult = configureSecondaryToolbar(settings.secondaryToolbar);
    (secondaryResult.ignored ?? []).forEach((item) => {
        ignored.push(`secondaryToolbar.${item.id ?? item}`);
    });

    if (permissionState.timeline) {
        uiState.timeline = true;
    }

    return { success: true, permissions: getPermissions(), ignored };
}

/**
 * Effective permission state, as plain JSON.
 */
export function getPermissions() {
    return JSON.parse(JSON.stringify(permissionState));
}
